import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Grid, Button, List, ListItem, 
    ListItemText, ListItemSecondaryAction, IconButton, 
    CircularProgress, Alert, Snackbar, TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SettingsService } from '../../api/services/settingsService';
import BookingSourceFormModal from '../../components/BookingSourceFormModal';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function BookingSourcesPage() {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Modal States
    const [modalOpen, setModalOpen] = useState(false);
    const [editingSource, setEditingSource] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [totalSources, setTotalSources] = useState(0);

    useEffect(() => {
        fetchBookingSources();
    }, [page, rowsPerPage]);

    const fetchBookingSources = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const data = await SettingsService.getBookingSources(tenantId, { page, limit: rowsPerPage });
            const sourceList = data?.data || data?.sources || data;
            setSources(Array.isArray(sourceList) ? sourceList : []);
            setTotalSources(extractTotalCount(data, sourceList));
        } catch (error) {
            console.error('Failed to fetch booking sources:', error);
            setNotification({ open: true, message: 'Failed to load booking sources.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (source = null) => {
        setEditingSource(source);
        setModalOpen(true);
    };

    const handleSaveSource = async (sourceData) => {
        setSaving(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            
            if (editingSource) {
                await SettingsService.updateBookingSource(editingSource.id, sourceData, tenantId);
                setNotification({ open: true, message: 'Booking source updated successfully!', severity: 'success' });
            } else {
                await SettingsService.createBookingSource(sourceData, tenantId);
                setNotification({ open: true, message: 'New booking source created successfully!', severity: 'success' });
            }
            
            setModalOpen(false);
            fetchBookingSources();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to save booking source', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };



    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Booking Sources</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => handleOpenModal()}
                >
                    Add Source
                </Button>
            </Box>

            <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Active Channels</Typography>
                            <IconButton onClick={fetchBookingSources} disabled={loading} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                        
                        <Paper sx={{ 
                            borderRadius: '12px', 
                            border: '1px solid #eaecf0', 
                            overflow: 'hidden',
                            boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05)'
                        }}>
                            <List sx={{ p: 0 }}>
                                {loading ? (
                                    <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} sx={{ mr: 2 }} />
                                        <Typography color="text.secondary">Loading sources...</Typography>
                                    </ListItem>
                                ) : sources.length > 0 ? (
                                    sources.map((source, idx) => (
                                        <ListItem key={source.id || idx} divider={idx !== sources.length - 1}>
                                            <ListItemText 
                                                primary={source.name} 
                                                secondary={source.description || source.status || (source.active ? 'Active' : 'Inactive')}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton 
                                                    edge="end" 
                                                    size="small" 
                                                    onClick={() => handleOpenModal(source)}
                                                    disabled={source.type === 'OTA'}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem sx={{ py: 4, justifyContent: 'center' }}>
                                        <Typography color="text.secondary">No booking sources configured</Typography>
                                    </ListItem>
                                )}
                            </List>
                            <TablePagination
                                component="div"
                                count={sources.length === 0 ? 0 : totalSources}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[20, 50, 100]}
                            />
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">About Booking Sources</Typography>
                            <Typography variant="body2">
                                Booking sources help you track where your reservations are coming from (e.g., Walk-in, Direct, Booking.com, Expedia). 
                                Active sources will appear in the booking forms.
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            </Paper>

            <BookingSourceFormModal 
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveSource}
                editingSource={editingSource}
                loading={saving}
            />



            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
